import React from 'react';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import { getCountryCodes, getLegal, identityIsLoading } from '../../reducer';
import { actionCreators as parameterActionCreators } from '../../reducer/parameters';
import { actionCreators as legalActionCreators } from '../../reducer/legal';
import styled from 'styled-components';
import { SecondaryButton } from './../common/elements/SecondaryButton';
import { Label } from './../common/elements/Label';
import { A } from './../common/elements/A';
import { Validation, regexToInputPattern } from '../../utils/validation';
import { TextInput } from './../common/elements/TextInput';
import { InputText } from './../common/elements/InputText';
import { Checkbox } from './../common/elements/Checkbox';
import { writeCookie } from '../../utils/cookieManager';
import { Loading } from '../common/blocks/Loading';
import { formatInternationalPhoneNumber } from '../../utils/formatting';
import withTextContext from '../../utils/withTextContext';

class RegisterPrivateCustomerForm extends React.Component {
	state = {
		// personId: '',
		countryCodes: [46],
		countryCode: '46',
		countryCodeValid: true,
		consent: false,
		terms: false,
		name: '',
		nameValid: false,
		department: '', // honeypot field
		departmentValid: false,
		email: '',
		emailValid: true,
		mobile: '',
		mobileValid: false,
		formStart: 0,
	};

	validators = {
		name: value => value.length > 0,
		mobile: value => value.length > 0 &&
			Validation.mobile.test(this.state.countryCode + formatInternationalPhoneNumber(value)),
		countryCode: (countryCodes, countryCode) => countryCodes.indexOf(+countryCode) !== -1,
		department: value => this.isCaptchaValid(value),
		email: value => value.length === 0 || (value.length > 0 && Validation.email.test(value)),
	};

	isFieldValid = (field, value) => this.validators[field](value);

	handleChange = target => {
		const { id, value } = target;

		this.setState({
			[id]: value,
			...(this.validators[id] && {
				[`${id}Valid`]: this.isFieldValid(id, value),
			}),
		});
	};

	onSubmit = () => {
		const { mobile, countryCodes, countryCode, name, department, email, formStart } = this.state;

		const validName = this.validators.name(name);
		const validCountryCode = this.validators.countryCode(countryCodes, countryCode);
		const validMobile = this.validators.mobile(mobile);
		const validEmail = this.validators.email(email);
		const validCaptcha = this.validators.department(department);

		const elapsed = Date.now() - formStart;

		if (validMobile && validCountryCode && validName && validCaptcha && validEmail && elapsed > 5000) {
			const formattedMobile = formatInternationalPhoneNumber(mobile);
			const newIdentity = {
				mobile: formattedMobile.length > 0 ? countryCode + formattedMobile : formattedMobile,
				email: email,
				name,
			};
			this.setLegalCookie();
			this.props.onRegister(newIdentity);
		} else {
			this.setState({
				mobileValid: validMobile,
				countryCodeValid: validCountryCode,
				nameValid: validName,
				captchaValid: validCaptcha,
				emailValid: validEmail,
			});
		}
	};

	setLegalCookie = () => {
		let legalCookieValue = {
			terms: this.props.legal.terms,
			privacy: this.props.legal.privacy,
			newsletterConsent: this.state.consent,
		};
		writeCookie('LEGAL_CONSENT', JSON.stringify(legalCookieValue), 365);
	};

	handleCheckboxClick = (name, value) => {
		this.setState({
			[name]: value,
		});
	};

	componentDidMount() {
		this.loadCountryCodes();
		this.loadLatestLegal();
		this.setFormStart();
	}

	setFormStart = () => {
		this.setState({
			formStart: Date.now(),
		});
	}

	loadCountryCodes = async () => {
		if (isEmpty(this.props.countryCodes)) {
			await this.props.getTravellerParameters();
		}
		const countryCodes = [...this.props.countryCodes];
		this.setState({ countryCodes });
	};

	loadLatestLegal = async () => {
		if (!this.props.legal) {
			await this.props.getLatestLegal();
		}
	};

	countryCodePattern = regexToInputPattern(Validation.countryCode);
	mobilePattern = regexToInputPattern(Validation.mobile);

	isNotValid = () =>
		!this.state.terms ||
		!this.state.mobileValid ||
		!this.state.mobile ||
		!this.state.name ||
		!this.isCaptchaValid(this.state.department);

	isCaptchaValid = c => c.trim() === "";

	render() {
		const { terms, privacy } = !!this.props.legal ? this.props.legal : '#';
		const { identityIsLoading, texts } = this.props;
		const {
			department,
			nameValid,
			emailValid,
			departmentValid,
			mobileValid,
		} = this.state;

		return (
			<>
				<h1 className="h2">Skapa konto</h1>
				<p>{texts.private.createaccount.createaccountinfo.infotext}</p>

				<FieldContainer>
					<div className="form-group">
						<Label htmlFor="mobile">Mobilnummer*</Label>
						<PhoneInputGroup className="input-group">
							<TextInputCountryCode
								type="number"
								value={this.state.countryCode}
								handleChange={this.handleChange}
								id="countryCode"
								maxLength="3"
								minLength="1"
								pattern={this.countryCodePattern}
								validateOnChange={true}
								disabled={identityIsLoading}
								autoComplete="tel-country-code"
								isError={!this.state.countryCodeValid}
								errorMessage={this.state.countryCodeValid ? null : "Ej tillåten landskod!."}
								style={{minWidth: "5rem"}}
							/>
							<TextInputPhone
								type="tel"
								value={this.state.mobile}
								handleChange={this.handleChange}
								id="mobile"
								disabled={identityIsLoading}
								autoComplete="tel-national"
								isError={!mobileValid}
								errorMessage={!mobileValid ? "Telefonnummer måste anges i formatet 701234567" : "Vänligen skriv ett korrekt telefonnummer."}
								isFullWidth
								offset="5rem"
								helperText="Anges i följande format: 701234567"
							/>
						</PhoneInputGroup>
					</div>

					<div className="form-group">
						<Label htmlFor="email">E-post</Label>
						<TextInputEmail
							type="email"
							value={this.state.email}
							handleChange={this.handleChange}
							id="email"
							disabled={identityIsLoading}
							autoComplete="email"
							isError={!emailValid}
							errorMessage={"Du måste ange en e-postadress som innehåller ett @-tecken."}
							helperText="Ska innehålla ett @. Format: exempel.exempel@mejl.com"
						/>
					</div>

					<div className="form-group">
						<Label htmlFor="name">Förnamn och Efternamn*</Label>
						<TextInputName
							type="text"
							value={this.state.name}
							handleChange={this.handleChange}
							id="name"
							disabled={identityIsLoading}
							autoComplete="name"
							isError={!nameValid}
							errorMessage={"Ange ett namn"}
							helperText="Förnamn och Efternamn: Anders Andersson"
						/>
					</div>
					<div className="form-group" style={{ "position": "absolute", "left": "-9999px" }} aria-hidden="true">
						<TextInputName
							type="text"
							value={department}
							handleChange={this.handleChange}
							id="department"
							disabled={identityIsLoading}
							autoComplete="off"
							isError={!departmentValid}
							tabIndex="-1"
						/>
					</div>
				</FieldContainer>

				<div className="checkbox">
					<Label>
						<Checkbox
							id="consent"
							name="consent"
							checked={this.state.consent}
							onCheck={this.handleCheckboxClick}
							disabled={identityIsLoading}
						/>{' '}
						Ja tack! jag vill gärna ta del av kampanjer och nyheter
					</Label>
				</div>

				<div className="checkbox">
					<Label>
						<Checkbox
							id="terms"
							name="terms"
							checked={this.state.terms}
							onCheck={this.handleCheckboxClick}
							disabled={identityIsLoading}
						/>{' '}
						Jag godkänner{' '}
						<A href={terms} target="_blank" rel="noopener noreferrer">
							avtal
						</A>{' '}
						och{' '}
						<A href={privacy} target="_blank" rel="noopener noreferrer">
							integritetspolicy
						</A>
						*
					</Label>
				</div>

				<SecondaryButton
					disabled={identityIsLoading || this.isNotValid()}
					onClick={this.onSubmit}
				>
					Skapa konto
				</SecondaryButton>

				{identityIsLoading && <Loading text="Ditt konto skapas..." />}
			</>
		);
	}
}

const FieldContainer = styled.div`
	text-align: left;
`;

const PhoneInputGroup = styled.div`
	display: flex;
`;

const TextInputName = styled(InputText)`
	width: 100%;
	min-width: 55px;
`;

const TextInputEmail = styled(InputText)`
	width: 100%;
	min-width: 55px;
`;

const TextInputCountryCode = styled(TextInput)`
	width: 55px;
	border-top-right-radius: 0;
	border-bottom-right-radius: 0;
	text-align: center;
`;

const TextInputPhone = styled(InputText)`
	border-top-left-radius: 0;
	border-bottom-left-radius: 0;
	border-left: 0;
	flex: 1;
	min-width: 55px;
`;

function mapStateToProps(store) {
	return {
		countryCodes: getCountryCodes(store),
		legal: getLegal(store),
		identityIsLoading: identityIsLoading(store),
	};
}

export default withTextContext(
	connect(mapStateToProps, {
		...parameterActionCreators,
		...legalActionCreators,
	})(RegisterPrivateCustomerForm)
);
